import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { BellRing, Check } from "lucide-react";
import {
  NOTIFICATION_ROUTES,
  REMINDER_TEMPLATES,
  sendReminderResponseSchema,
  type ReminderKind,
} from "@nh/shared";
import { authFetch } from "@/shared/api/session";
import { Button } from "@/shared/ui/core";
import { cn } from "@/shared/lib/cn";

/**
 * ═══════════════════════════════════════════════════════════════════
 * El botón de RECORDAR — un componente para mandar distintos
 * recordatorios a socios y franquiciatarios (Anahí, 27-ago).
 *
 * Se planta al lado del pendiente que lo motiva (sin banco, sin KYC…)
 * y manda la notificación del catálogo a la campana de esa persona.
 * QUIEN LO PINTA decide cuándo desaparece: el botón de banco vive
 * dentro del `!hasPayoutMethod`, así que registrar la cuenta lo borra
 * solo. Y el servidor repite la regla: recordar algo ya resuelto se
 * rechaza con la razón.
 * ═══════════════════════════════════════════════════════════════════
 */
export function SendReminderButton({
  userId,
  kind,
  message,
  className,
  iconOnly = false,
}: {
  userId: string;
  kind: ReminderKind;
  /** Solo para `custom` (u opcional para matizar la plantilla). */
  message?: string;
  className?: string;
  /**
   * Solo la campana, sin texto (Anahí, 28-ago). En una tabla, el rótulo
   * completo repetido en cada fila ocupa media pantalla y no dice nada
   * nuevo: la columna ya se llama "Recordar". El nombre accesible y el
   * `title` siguen completos, así que quien navega con lector de
   * pantalla —o quien pasa el ratón— sabe exactamente qué manda.
   */
  iconOnly?: boolean;
}) {
  const { t } = useTranslation();
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const enviar = useMutation({
    mutationFn: () =>
      authFetch(NOTIFICATION_ROUTES.adminSend, sendReminderResponseSchema, {
        baseUrl: "",
        method: "POST",
        body: { userId, kind, ...(message ? { message } : {}) },
      }),
    onSuccess: () => {
      setSent(true);
      setError(null);
      window.setTimeout(() => setSent(false), 4000);
    },
    onError: (cause) =>
      setError(
        cause instanceof Error && cause.message
          ? cause.message
          : t("No se pudo enviar el recordatorio"),
      ),
  });

  const label = sent ? t("Recordatorio enviado") : t(REMINDER_TEMPLATES[kind].label);

  if (iconOnly) {
    return (
      <span className={className}>
        <button
          type="button"
          title={error ?? label}
          aria-label={label}
          disabled={sent || enviar.isPending}
          onClick={() => {
            setError(null);
            enviar.mutate();
          }}
          className={cn(
            "inline-flex h-8 w-8 items-center justify-center rounded-full border transition",
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ds-violet-pop/40",
            error
              ? "border-red-200 bg-red-50 text-red-600"
              : sent
                ? "border-ds-border bg-ds-success-soft text-ds-ink"
                : "border-ds-border bg-white text-ds-violet-pop hover:bg-ds-lavender-bg",
            enviar.isPending && "opacity-60",
          )}
        >
          {sent ? <Check className="h-4 w-4" /> : <BellRing className="h-4 w-4" />}
        </button>
      </span>
    );
  }

  return (
    <span className={className}>
      <Button
        variant="secondary"
        size="sm"
        leftIcon={sent ? <Check /> : <BellRing />}
        loading={enviar.isPending}
        disabled={sent}
        onClick={() => {
          setError(null);
          enviar.mutate();
        }}
      >
        {label}
      </Button>
      {error && <span className="mt-1 block text-[11px] font-medium text-red-600">{error}</span>}
    </span>
  );
}
